/**
 * FieldModeRequestsPage - Hibrit çalışanların saha/ofis modu geçiş taleplerini
 * onaylama/reddetme sayfası (OvertimeManagement/OvertimePendingContent deseninde).
 * Sekmeler: "Bekleyen Talepler" ve şirket genelindeki "Saha Kayıtları" (çıkış → dönüş oturumları).
 */

import React, { useState, useCallback, useEffect } from 'react';
import { message, Spin, Button, Tabs } from 'antd';
import { Link } from 'react-router-dom';
import { all_routes } from '../../../router/all_routes';
import fieldModeRequestService from '../../../services/fieldModeRequest/fieldModeRequest.service';
import { FieldModeRequestDto } from '../../../types/fieldModeRequest.types';
import { handleApiError } from '../../../utils/errorHandler';
import { useTranslation } from 'react-i18next';
import FieldModeRequestCard from './components/FieldModeRequestCard';
import FieldModeReviewModal from './components/FieldModeReviewModal';
import FieldModeAutoApproveSettings from './components/FieldModeAutoApproveSettings';
import FieldModeSessionsContent from './components/FieldModeSessionsContent';

type FieldModeTabKey = 'pending' | 'sessions';

const FieldModeRequestsPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;

  const [activeTab, setActiveTab] = useState<FieldModeTabKey>('pending');
  const [sessionsRefreshSignal, setSessionsRefreshSignal] = useState<number>(0);

  const [requests, setRequests] = useState<FieldModeRequestDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [actionLoading, setActionLoading] = useState<boolean>(false);

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<'approve' | 'reject' | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<FieldModeRequestDto | null>(null);

  const fetchPendingRequests = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fieldModeRequestService.getPendingFieldModeRequests();
      setRequests(result);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchPendingRequests();
  }, [fetchPendingRequests]);

  const openReview = (request: FieldModeRequestDto, action: 'approve' | 'reject') => {
    setSelectedRequest(request);
    setModalAction(action);
    setModalVisible(true);
  };

  const handleConfirm = async (requestId: number, action: 'approve' | 'reject', rejectionReason?: string) => {
    setActionLoading(true);
    try {
      const trimmedReason = rejectionReason?.trim();
      await fieldModeRequestService.reviewFieldModeRequest(requestId, {
        action,
        ...(action === 'reject' && trimmedReason ? { rejectionReason: trimmedReason } : {}),
      });
      message.success(action === 'approve' ? 'Talep onaylandı' : 'Talep reddedildi');
      setModalVisible(false);
      setSelectedRequest(null);
      setModalAction(null);
      fetchPendingRequests();
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Saha Modu Talepleri</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item active">Saha Modu Talepleri</li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <Button
              icon={<i className="ti ti-reload me-2" />}
              onClick={() =>
                activeTab === 'pending' ? fetchPendingRequests() : setSessionsRefreshSignal((n) => n + 1)
              }
              loading={activeTab === 'pending' ? loading : false}
            >
              Yenile
            </Button>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <Tabs
              activeKey={activeTab}
              onChange={(key) => setActiveTab(key as FieldModeTabKey)}
              size="large"
              type="card"
              items={[
                {
                  key: 'pending',
                  label: (
                    <span>
                      <i className="ti ti-clock-hour-4 me-2" />
                      Bekleyen Talepler{requests.length > 0 ? ` (${requests.length})` : ''}
                    </span>
                  ),
                  children: loading ? (
                    <div className="text-center py-5">
                      <Spin size="large" />
                    </div>
                  ) : requests.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i className="ti ti-map-off" style={{ fontSize: '64px', color: '#d9d9d9' }} />
                      </div>
                      <h5 className="mb-2">Bekleyen saha modu talebi bulunmuyor</h5>
                      <p className="text-muted mb-0">Yeni talepler geldiğinde burada görüntülenecek</p>
                    </div>
                  ) : (
                    <div className="row g-3">
                      {requests.map((request) => (
                        <div key={request.id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12">
                          <FieldModeRequestCard
                            request={request}
                            onApprove={(r) => openReview(r, 'approve')}
                            onReject={(r) => openReview(r, 'reject')}
                          />
                        </div>
                      ))}
                    </div>
                  ),
                },
                {
                  key: 'sessions',
                  label: (
                    <span>
                      <i className="ti ti-route me-2" />
                      Saha Kayıtları
                    </span>
                  ),
                  children: <FieldModeSessionsContent refreshSignal={sessionsRefreshSignal} />,
                },
              ]}
            />
          </div>
        </div>

        <FieldModeReviewModal
          visible={modalVisible}
          action={modalAction}
          request={selectedRequest}
          onConfirm={handleConfirm}
          onCancel={() => {
            setModalVisible(false);
            setSelectedRequest(null);
            setModalAction(null);
          }}
          loading={actionLoading}
        />

        {activeTab === 'pending' && (
          <div className="mt-4">
            <FieldModeAutoApproveSettings />
          </div>
        )}
      </div>
    </div>
  );
};

export default FieldModeRequestsPage;
